import type { InventoryTrendPoint } from "../api/rest";

/**
 * ADR 362 — pure helpers behind the run-over-run section. The series are
 * plain arrays with `null` gaps (a run without a WMS verdict), so the charts
 * break the line instead of drawing through a point that does not exist.
 */

export interface TrendSeries {
  agreement: (number | null)[];
  coverage: (number | null)[];
  divergent: (number | null)[];
  addresses: (number | null)[];
}

export function seriesOf(points: InventoryTrendPoint[]): TrendSeries {
  return {
    agreement: points.map((p) => p.judgement?.agreementPct ?? null),
    // ADR 410: read addresses over the master — present with or without a WMS.
    coverage: points.map((p) => p.coveragePct ?? null),
    divergent: points.map((p) => p.judgement?.divergent ?? null),
    addresses: points.map((p) => p.addressesScanned ?? null),
  };
}

/** Last non-null value and its change against the previous non-null one
 *  (null delta = nothing earlier to compare with). */
export function latestDelta(values: (number | null)[]): { value: number; delta: number | null } | null {
  const idx: number[] = [];
  values.forEach((v, i) => { if (v !== null) idx.push(i); });
  if (idx.length === 0) return null;
  const last = values[idx[idx.length - 1]] as number;
  if (idx.length < 2) return { value: last, delta: null };
  const prev = values[idx[idx.length - 2]] as number;
  return { value: last, delta: last - prev };
}

export interface ChartGeometry {
  /** SVG path — one `M…L…` run per contiguous non-null stretch. */
  path: string;
  /** Marker centers, null where the series has a gap. */
  points: ({ x: number; y: number } | null)[];
  /** Domain the y scale used, so gridlines can be drawn on the same scale. */
  domain: [number, number];
  x: (i: number) => number;
  y: (v: number) => number;
}

/**
 * Lay a series out in a `width × height` box with `pad` inset (`padLeft`
 * widens the left inset for a gridline-label gutter). Percent
 * series pin the domain to 0–100 (a chart that rescales 97→98 into a cliff
 * lies); count series take 0..max.
 */
export function layout(
  values: (number | null)[], width: number, height: number, pad: number, percent: boolean,
  padLeft: number = pad,
): ChartGeometry {
  const n = values.length;
  const present = values.filter((v): v is number => v !== null);
  const max = percent ? 100 : Math.max(1, ...present);
  const domain: [number, number] = [0, max];
  const innerW = width - padLeft - pad;
  const innerH = height - pad * 2;
  const x = (i: number) => (n <= 1 ? padLeft + innerW / 2 : padLeft + (innerW * i) / (n - 1));
  const y = (v: number) => pad + innerH - (innerH * Math.min(Math.max(v, 0), max)) / max;
  const points = values.map((v, i) => (v === null ? null : { x: x(i), y: y(v) }));
  let path = "";
  let open = false;
  points.forEach((p) => {
    if (!p) { open = false; return; }
    path += `${open ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
    open = true;
  });
  return { path, points, domain, x, y };
}

/** Signed, locale-formatted delta for a stat tile ("+2,3", "−0,8", "0"). */
export function formatDelta(delta: number, locale: string, digits = 1): string {
  const rounded = Number(delta.toFixed(digits));
  const abs = Math.abs(rounded).toLocaleString(locale, { maximumFractionDigits: digits });
  if (rounded > 0) return `+${abs}`;
  if (rounded < 0) return `−${abs}`;
  return abs;
}

/** Point label for the axis: the run's name if it has one, else its date. */
export function pointLabel(p: InventoryTrendPoint, locale: string): string {
  if (p.name) return p.name;
  const iso = p.startedAt ?? p.endedAt;
  return iso ? shortDate(iso, locale) : p.runId.slice(0, 8);
}

export function shortDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "2-digit" });
}

export function shortTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/** Axis label for point {@code i}: its date, or its time when the previous point
 *  fell on the same day — six passes of one afternoon must not read as six
 *  copies of one date. */
export function axisLabel(points: readonly InventoryTrendPoint[], i: number, locale: string): string {
  const iso = points[i]?.startedAt;
  if (!iso) return "";
  const prev = i > 0 ? points[i - 1]?.startedAt : undefined;
  if (prev && shortDate(prev, locale) === shortDate(iso, locale)) return shortTime(iso, locale);
  return shortDate(iso, locale);
}
